import React from 'react';
import { InputAdornment, MenuItem, TextField } from '@mui/material';
import InputMask from 'react-input-mask';
import PopupBase from '../../../popup/base/PopupBase';
import CustomButton from '../../../components/buttons/CustomButton';
import SignatureField from './SignatureField';
import PublicScheduleBlock from './PublicScheduleBlock';
import ResourceScheduleBlock from './ResourceScheduleBlock';
import styles from './ResourceFormPopup.module.css';

/** Bloco do formulário com subtítulo. */
function Section({ title, children }) {
    return (
        <section className={styles.section}>
            <h4 className={styles.sectionTitle}>{title}</h4>
            {children}
        </section>
    );
}

/**
 * Criar ou editar uma agenda. O estado do formulário mora na página (ResourceList):
 * aqui só a apresentação, em blocos (identificação, agenda do dia, documentos e
 * horário de atendimento e agendamento público). A sala padrão só aparece quando a empresa tem salas.
 */
function ResourceFormPopup({
    isCreate, formData, labelError, companyScheduleEnabled, companyConfig, rooms = [], onInputChange, onFieldValue, slug, previewSlug, signature, onCopied, onSubmit, onClose,
}) {
    const content = (
        <div className={styles.content}>
            <Section title="Identificação">
                <div className={styles.field}>
                    <label className={styles.label} htmlFor="resource-label">Nome da agenda *</label>
                    <TextField
                        id="resource-label"
                        name="label"
                        value={formData.label}
                        onChange={onInputChange}
                        fullWidth
                        size="small"
                        required
                        error={!!labelError}
                        helperText={labelError || undefined}
                        autoFocus={isCreate}
                        placeholder="Ex.: Dra. Ana Souza"
                        className={styles.input}
                    />
                </div>
                <div className={styles.field}>
                    <label className={styles.label} htmlFor="resource-expertise">Especialidade</label>
                    <TextField
                        id="resource-expertise"
                        name="expertise"
                        value={formData.expertise}
                        onChange={onInputChange}
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                        placeholder="Descreva a especialidade"
                        className={styles.input}
                    />
                </div>
                {rooms.length > 0 && (
                    <div className={styles.field}>
                        <label className={styles.label} htmlFor="resource-default-room">Sala padrão</label>
                        <TextField
                            id="resource-default-room"
                            name="default_room_id"
                            select
                            value={formData.default_room_id || ''}
                            onChange={onInputChange}
                            fullWidth
                            size="small"
                            className={styles.input}
                        >
                            <MenuItem value="">Sem sala padrão</MenuItem>
                            {rooms.map((room) => (
                                <MenuItem key={room.Id} value={String(room.Id)}>{room.Label}</MenuItem>
                            ))}
                        </TextField>
                        <span className={styles.hint}>
                            Onde esta agenda costuma atender. Vem preenchida em cada agendamento novo; no Início dá para trocar a sala só de um dia.
                        </span>
                    </div>
                )}
            </Section>

            <Section title="Horário de atendimento">
                <ResourceScheduleBlock
                    schedule={formData.schedule_config}
                    companyConfig={companyConfig}
                    onChange={(value) => onFieldValue('schedule_config', value)}
                />
            </Section>

            <Section title="Contato">
                <div className={styles.field}>
                    <span className={styles.label}>WhatsApp</span>
                    <div className={styles.phoneRow}>
                        <TextField
                            name="phoneCountryCode"
                            value={formData.phoneCountryCode}
                            onChange={onInputChange}
                            size="small"
                            className={`${styles.input} ${styles.ddi}`}
                            inputProps={{ 'aria-label': 'DDI' }}
                            InputProps={{ startAdornment: <InputAdornment position="start">+</InputAdornment> }}
                        />
                        <InputMask mask="(99) 99999-9999" value={formData.phoneNumber} onChange={onInputChange}>
                            {(inputProps) => (
                                <TextField
                                    {...inputProps}
                                    name="phoneNumber"
                                    fullWidth
                                    size="small"
                                    placeholder="(11) 99999-9999"
                                    className={styles.input}
                                />
                            )}
                        </InputMask>
                    </div>
                    <span className={styles.hint}>Recebe a agenda do dia quando a empresa usa o próprio número.</span>
                </div>
                <div className={styles.field}>
                    <label className={styles.label} htmlFor="resource-email">E-mail</label>
                    <TextField
                        id="resource-email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={onInputChange}
                        fullWidth
                        size="small"
                        placeholder="nome@exemplo.com"
                        helperText="Recebe a agenda do dia por e-mail."
                        className={styles.input}
                    />
                </div>
            </Section>

            <Section title="Documentos">
                <div className={styles.field}>
                    <label className={styles.label} htmlFor="resource-regulator">Órgão regulador</label>
                    <TextField
                        id="resource-regulator"
                        name="regulator"
                        value={formData.regulator}
                        onChange={onInputChange}
                        fullWidth
                        size="small"
                        placeholder="Ex.: CREFITO-3 12345"
                        className={styles.input}
                    />
                </div>
                <div className={styles.field}>
                    <span className={styles.label}>Assinatura</span>
                    <SignatureField {...signature} />
                </div>
            </Section>

            <Section title="Agendamento online">
                <PublicScheduleBlock
                    enabled={formData.public_schedule_enabled}
                    companyEnabled={companyScheduleEnabled}
                    bio={formData.public_schedule_bio}
                    slug={slug}
                    previewSlug={previewSlug}
                    onToggle={(value) => onFieldValue('public_schedule_enabled', value)}
                    onBioChange={(value) => onFieldValue('public_schedule_bio', value)}
                    onCopied={onCopied}
                />
            </Section>
        </div>
    );

    const footer = (
        <div className={styles.footer}>
            <CustomButton style="primary" variant="text" label="Cancelar" onClick={onClose} />
            <CustomButton
                style="main"
                variant="contained"
                label={isCreate ? 'Criar agenda' : 'Salvar'}
                onClick={onSubmit}
            />
        </div>
    );

    return (
        <PopupBase
            Title={isCreate ? 'Nova agenda' : 'Editar agenda'}
            Content={content}
            Footer={footer}
            OnClose={onClose}
            maxWidth="sm"
        />
    );
}

export default ResourceFormPopup;
